/* eslint-disable better-mutation/no-mutation */
import { createApi } from '@reduxjs/toolkit/query/react';
import { isomorphicWebApiBaseQuery } from 'tradera/state/services/utils/webapi-base-query';
import { PaidStatuses } from 'tradera/constants/paid-statuses';

import type {
    SellerSoldPurchaseOrdersIncludingExtendedLifeApiResponse,
    CancelShipmentApiRequest,
    CancelShipmentApiResponse,
    UpdateShippingPriceApiRequest,
    UpdateShippingPriceApiResponse,
    SoldPageApiRequest,
    DeleteShipmentApiResponse,
    DeleteShipmentApiRequest
} from 'tradera/state/services/types/webapi-mytradera-generated';

import type {
    SetItemShippingBookedApiRequest,
    SetItemShippingBookedApiResponse,
    MarkAsPaidBySellerApiRequest,
    MarkAsPaidBySellerApiResponse,
    SetItemShippedApiRequest,
    SetItemShippedApiResponse,
    SetTransactionVisibilityApiRequest,
    SetTransactionVisibilityApiResponse
} from 'tradera/state/services/types/webapi-member-generated';
import qs from 'qs';
import type {
    ChangeItemsShippingRequest,
    GetChangeItemsShippingViewModelApiResponse
} from 'tradera/state/services/types/webapi-selling-generated';

export const MySoldTagTypes = {
    SoldPurchaseOrders: 'SoldPurchaseOrders'
} as const;

export const getMySoldItemsCacheKeys: Set<SoldPageApiRequest> = new Set();

export const mySoldApi = createApi({
    reducerPath: 'mytSoldApi',
    baseQuery: isomorphicWebApiBaseQuery,
    tagTypes: Object.values(MySoldTagTypes),
    endpoints: (builder) => ({
        getMySoldItems: builder.query<
            SellerSoldPurchaseOrdersIncludingExtendedLifeApiResponse,
            SoldPageApiRequest
        >({
            query: (query) => {
                getMySoldItemsCacheKeys.add(query);
                return {
                    url: '/api/webapi/mytradera/web/sold-page',
                    method: 'POST',
                    body: query
                };
            },
            providesTags: (_response, _error, request) => [
                {
                    type: MySoldTagTypes.SoldPurchaseOrders,
                    id: JSON.stringify(request.sellerFilters)
                }
            ]
        }),
        markAsPaidBySeller: builder.mutation<
            MarkAsPaidBySellerApiResponse,
            MarkAsPaidBySellerApiRequest
        >({
            query: (payload) => ({
                url: '/api/webapi/member/selling/marked-as-paid-by-seller',
                method: 'POST',
                body: payload
            }),
            async onQueryStarted(
                { purchaseOrderId, paidConfirmed, ...patch },
                { dispatch, queryFulfilled }
            ) {
                const patchResults = [];
                for (const cacheKey of getMySoldItemsCacheKeys) {
                    const patchResult = dispatch(
                        mySoldApi.util.updateQueryData(
                            'getMySoldItems',
                            cacheKey,
                            (draft) => {
                                const purchaseOrder =
                                    draft.purchaseOrdersOneOf?.find(
                                        ({ purchaseOrder }) =>
                                            purchaseOrder?.purchaseOrderId ===
                                            purchaseOrderId
                                    )?.purchaseOrder;
                                if (purchaseOrder?.state) {
                                    purchaseOrder.state.isPaid = paidConfirmed;
                                    purchaseOrder.state.paidStatus =
                                        paidConfirmed
                                            ? PaidStatuses.MARKED_AS_PAID_BY_SELLER
                                            : PaidStatuses.UNPAID;
                                }
                                Object.assign(draft, patch);
                            }
                        )
                    );
                    patchResults.push(patchResult);
                }
                try {
                    await queryFulfilled;
                } catch {
                    for (const patch of patchResults) {
                        patch.undo();
                    }
                }
            }
        }),
        markAsShippedBySeller: builder.mutation<
            SetItemShippedApiResponse,
            SetItemShippedApiRequest
        >({
            query: (payload) => ({
                url: '/api/webapi/member/selling/item-shipped',
                method: 'POST',
                body: payload
            }),
            async onQueryStarted(
                { purchaseOrderId, shipped, ...patch },
                { dispatch, queryFulfilled }
            ) {
                const patchResults = [];
                for (const cacheKey of getMySoldItemsCacheKeys) {
                    const patchResult = dispatch(
                        mySoldApi.util.updateQueryData(
                            'getMySoldItems',
                            cacheKey,
                            (draft) => {
                                const purchaseOrder =
                                    draft.purchaseOrdersOneOf?.find(
                                        ({ purchaseOrder }) =>
                                            purchaseOrder?.purchaseOrderId ===
                                            purchaseOrderId
                                    )?.purchaseOrder;
                                if (purchaseOrder?.state) {
                                    purchaseOrder.state.isShipped = shipped;
                                }
                                Object.assign(draft, patch);
                            }
                        )
                    );
                    patchResults.push(patchResult);
                }
                try {
                    await queryFulfilled;
                } catch {
                    for (const patch of patchResults) {
                        patch.undo();
                    }
                }
            }
        }),
        cancelShipping: builder.mutation<
            CancelShipmentApiResponse,
            CancelShipmentApiRequest
        >({
            query: (payload) => ({
                url: '/api/webapi/mytradera/shipping/cancelshipment',
                method: 'POST',
                body: payload
            }),
            async onQueryStarted({ shipmentId }, { dispatch, queryFulfilled }) {
                const patchResults = [];
                for (const cacheKey of getMySoldItemsCacheKeys) {
                    const patchResult = dispatch(
                        mySoldApi.util.updateQueryData(
                            'getMySoldItems',
                            cacheKey,
                            (draft) => {
                                const purchaseOrder =
                                    draft.purchaseOrdersOneOf?.find(
                                        ({ purchaseOrder }) =>
                                            purchaseOrder?.shipping?.shipping
                                                ?.shipmentId === shipmentId
                                    )?.purchaseOrder;
                                if (
                                    purchaseOrder?.state &&
                                    purchaseOrder.shipping?.shipping
                                ) {
                                    purchaseOrder.state.isShippingBooked =
                                        false;
                                    purchaseOrder.shipping.shipping = {
                                        ...purchaseOrder.shipping.shipping,
                                        isCancelled: true,
                                        isCancellable: false,
                                        url: null
                                    };
                                }
                                Object.assign(draft, {
                                    ...draft,
                                    purchaseOrdersOneOf: [
                                        ...(draft.purchaseOrdersOneOf?.filter(
                                            (p) =>
                                                p.purchaseOrder
                                                    ?.purchaseOrderId !==
                                                purchaseOrder?.purchaseOrderId
                                        ) ?? []),
                                        purchaseOrder
                                    ]
                                });
                            }
                        )
                    );
                    patchResults.push(patchResult);
                }
                try {
                    await queryFulfilled;
                } catch {
                    for (const patch of patchResults) {
                        patch.undo();
                    }
                }
            }
        }),
        deleteShipping: builder.mutation<
            DeleteShipmentApiResponse,
            DeleteShipmentApiRequest
        >({
            query: (payload) => ({
                url: '/api/webapi/mytradera/shipping/deleteshipment',
                method: 'POST',
                body: payload
            }),
            async onQueryStarted(
                { shipmentId, ...patch },
                { dispatch, queryFulfilled }
            ) {
                const patchResults = [];
                for (const cacheKey of getMySoldItemsCacheKeys) {
                    const patchResult = dispatch(
                        mySoldApi.util.updateQueryData(
                            'getMySoldItems',
                            cacheKey,
                            (draft) => {
                                const purchaseOrder =
                                    draft.purchaseOrdersOneOf?.find(
                                        ({ purchaseOrder }) =>
                                            purchaseOrder?.shipping?.shipping
                                                ?.shipmentId === shipmentId
                                    )?.purchaseOrder;
                                if (
                                    purchaseOrder?.state &&
                                    purchaseOrder.shipping?.unpaidShipping
                                ) {
                                    purchaseOrder.state.hasUnpaidShipping =
                                        false;
                                    purchaseOrder.shipping.unpaidShipping =
                                        null;
                                }
                                Object.assign(draft, patch);
                            }
                        )
                    );
                    patchResults.push(patchResult);
                }
                try {
                    await queryFulfilled;
                } catch {
                    for (const patch of patchResults) {
                        patch.undo();
                    }
                }
            }
        }),
        setIsHidden: builder.mutation<
            SetTransactionVisibilityApiResponse,
            SetTransactionVisibilityApiRequest
        >({
            query: (payload) => ({
                url: '/api/webapi/member/transaction-visibility',
                method: 'POST',
                body: payload
            }),
            invalidatesTags: (_response, _error, request) =>
                request.hidden
                    ? // request.hidden here means that we have hidden an item
                      // from a list where we do not show hidden items.
                      // Only invalidate requests that are hidden="True".
                      // This is because the updated visibility might not have
                      // propageted all the way to Elastic yet, so if we invalidate
                      // the request where we show non-hidden items we request an
                      // updated list which still contains the item that the user
                      // just hid. Hiding it in the client is handled by the
                      // onQueryStarted optimistic update
                      [...getMySoldItemsCacheKeys.values()]
                          .filter(
                              (cacheKey) =>
                                  cacheKey.sellerFilters?.hidden === 'True'
                          )
                          .map((cacheEntry) => ({
                              type: MySoldTagTypes.SoldPurchaseOrders,
                              id: JSON.stringify(cacheEntry.sellerFilters)
                          }))
                    : // and reversed for lists with hidden items, if we have
                      // un-hidden an item, we should update all lists where it might
                      // show up, i.e lists that contains items that aren't hidden
                      [...getMySoldItemsCacheKeys.values()]
                          .filter(
                              (cacheKey) =>
                                  cacheKey.sellerFilters?.hidden === 'False'
                          )
                          .map((cacheEntry) => ({
                              type: MySoldTagTypes.SoldPurchaseOrders,
                              id: JSON.stringify(cacheEntry.sellerFilters)
                          })),
            async onQueryStarted(patch, { dispatch, queryFulfilled }) {
                const patchResults = [];
                for (const cacheKey of getMySoldItemsCacheKeys) {
                    if (
                        // When on the hidden tab
                        (cacheKey.sellerFilters?.hidden === 'True' &&
                            !patch.hidden) || // When not on the hidden tab
                        (cacheKey.sellerFilters?.hidden === 'False' &&
                            patch.hidden)
                    ) {
                        const patchResult = dispatch(
                            mySoldApi.util.updateQueryData(
                                'getMySoldItems',
                                cacheKey,
                                (draft) => {
                                    const updatedItems =
                                        draft.purchaseOrdersOneOf?.filter(
                                            ({
                                                purchaseOrder,
                                                extendedLifePurchaseOrder
                                            }) =>
                                                purchaseOrder?.purchaseOrderId !==
                                                    patch.purchaseOrderId &&
                                                extendedLifePurchaseOrder?.purchaseOrderId !==
                                                    patch.purchaseOrderId
                                        );

                                    Object.assign(draft, {
                                        purchaseOrdersOneOf: updatedItems
                                    });
                                }
                            )
                        );
                        patchResults.push(patchResult);
                    }
                }

                try {
                    await queryFulfilled;
                } catch {
                    for (const patch of patchResults) {
                        patch.undo();
                    }
                }
            }
        }),
        getChangeItemsShipping: builder.query<
            GetChangeItemsShippingViewModelApiResponse,
            ChangeItemsShippingRequest
        >({
            query: ({ purchaseOrderId, fromCountry, toCountry }) =>
                `/api/webapi/selling/shipping/change-items-shipping?${qs.stringify(
                    {
                        purchaseOrderId,
                        fromCountry,
                        toCountry
                    }
                )}`
        }),
        updateShippingPrice: builder.mutation<
            UpdateShippingPriceApiResponse,
            UpdateShippingPriceApiRequest
        >({
            query: (payload) => ({
                url: '/api/webapi/mytradera/shipping/update-shipping-price',
                method: 'POST',
                body: payload
            }),
            async onQueryStarted(
                {
                    purchaseOrderId,
                    price,
                    shippingProvider,
                    shippingProductId,
                    ...patch
                },
                { dispatch, queryFulfilled }
            ) {
                const patchResults = [];
                for (const cacheKey of getMySoldItemsCacheKeys) {
                    const patchResult = dispatch(
                        mySoldApi.util.updateQueryData(
                            'getMySoldItems',
                            cacheKey,
                            (draft) => {
                                const purchaseOrder =
                                    draft.purchaseOrdersOneOf?.find(
                                        ({ purchaseOrder }) =>
                                            purchaseOrder?.purchaseOrderId ===
                                            purchaseOrderId
                                    )?.purchaseOrder;
                                if (purchaseOrder) {
                                    purchaseOrder.shippingAmount = price;
                                    purchaseOrder.shipping = {};
                                    purchaseOrder.shipping.selectedShipping = {
                                        shippingProductId:
                                            shippingProductId ?? 0,
                                        shippingCost: price.toString(),
                                        shippingType: shippingProvider
                                    };
                                    if (
                                        purchaseOrder.totalAmount != null &&
                                        purchaseOrder.totalItemAmount != null
                                    ) {
                                        purchaseOrder.totalAmount =
                                            purchaseOrder.totalItemAmount +
                                            price;
                                    }
                                }
                                Object.assign(draft, patch);
                            }
                        )
                    );
                    patchResults.push(patchResult);
                }
                try {
                    await queryFulfilled;
                } catch {
                    for (const patch of patchResults) {
                        patch.undo();
                    }
                }
            }
        }),
        markAsBooked: builder.mutation<
            SetItemShippingBookedApiResponse,
            SetItemShippingBookedApiRequest
        >({
            query: (payload) => ({
                url: '/api/webapi/member/selling/item-shipping-booked',
                method: 'POST',
                body: payload
            }),
            async onQueryStarted(
                { purchaseOrderId, booked, ...patch },
                { dispatch, queryFulfilled }
            ) {
                const patchResults = [];
                for (const cacheKey of getMySoldItemsCacheKeys) {
                    const patchResult = dispatch(
                        mySoldApi.util.updateQueryData(
                            'getMySoldItems',
                            cacheKey,
                            (draft) => {
                                const purchaseOrder =
                                    draft.purchaseOrdersOneOf?.find(
                                        ({ purchaseOrder }) =>
                                            purchaseOrder?.purchaseOrderId ===
                                            purchaseOrderId
                                    )?.purchaseOrder;
                                if (purchaseOrder?.state) {
                                    purchaseOrder.state.isShippingBooked =
                                        booked;
                                }
                                Object.assign(draft, patch);
                            }
                        )
                    );
                    patchResults.push(patchResult);
                }
                try {
                    await queryFulfilled;
                } catch {
                    for (const patch of patchResults) {
                        patch.undo();
                    }
                }
            }
        })
    })
});

export const {
    useGetMySoldItemsQuery,
    useMarkAsPaidBySellerMutation,
    useMarkAsShippedBySellerMutation,
    useCancelShippingMutation,
    useDeleteShippingMutation,
    useSetIsHiddenMutation,
    useGetChangeItemsShippingQuery,
    useMarkAsBookedMutation,
    useUpdateShippingPriceMutation
} = mySoldApi;
